import type {
  IntakeChartEntrySection,
  IntakeDemographicField,
  PatientConsentKind,
  ReconcileSchedulingPatientRequest,
  ReconcileSchedulingPatientResponse,
  SchedulingIntakeReview,
  SchedulingIntakeSection,
  SchedulingIntakeSectionReview,
  SchedulingIntakeSubmission,
} from "@emr/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  intakeErrorMessage,
  isIntakeReviewClosed,
  isIntakeTargetChanged,
  newIntakeIdempotencyKey,
} from "../../api/schedulingIntake";
import { Glyph } from "../../components/IceBlueGlyphs";
import { useDemoScheduling } from "../../demo/DemoSchedulingProvider";
import { SchedulingRequestError } from "../../demo/schedulingErrors";
import {
  invalidatePatientQueries,
  usePatientProfileQuery,
} from "../patient-profile/usePatientProfile";
import {
  ChartSectionPanel,
  IntakeDetailsStep,
  IntakeDisclosure,
  IntakeEntriesPanel,
  IntakeVisitContextPanel,
} from "./IntakeAnswerPanels";
import { IntakeChartReconciler, WalkInChartDuplicates } from "./IntakeChartReconciler";
import {
  IntakeHealthCardPhoto,
  intakeHealthCardPhotoShown,
  useIntakeHealthCardPhotos,
} from "./IntakeHealthCardPhoto";
import {
  applicationLabel,
  CHART_ENTRY_SECTION_LABELS,
  INTAKE_FIELD_LABELS,
  intakeDetailReview,
  intakeEntriesNote,
  intakeEntryProposals,
  intakeFinishLabel,
  intakeReasonDecision,
  isDetailChoice,
  sectionReviewLabel,
} from "./intakeReviewModel";
import {
  AUDITED_READ,
  type IntakeReviewClient,
  schedulingIntakeKey,
  schedulingPatientMatchesKey,
} from "./intakeReviewQueries";
import {
  MESSAGING_CONSENT_STATE_LABELS,
  messagingDecisionPhrase,
  PATIENT_MESSAGE_CHANNEL_LABELS,
  PATIENT_MESSAGE_KIND_LABELS,
  PATIENT_MESSAGE_STATUS_WORDS,
} from "./patientMessageCopy";
import { ScheduleModal } from "./ScheduleModal";
import { formatInClinic } from "./scheduleTime";
import "./intake-review.css";

const CHART_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

const ENTRY_SECTIONS = ["allergies", "medications", "pmh"] as const;
const NO_PICKS: ReadonlySet<string> = new Set();

const CONSENT_KIND_LABELS: Readonly<Record<PatientConsentKind, string>> = {
  privacy_notice: "Terms and privacy notice",
  electronic_messaging: "Email and text messages",
};

const SECTION_TITLES: Readonly<Record<SchedulingIntakeSection, string>> = {
  administrative: "Details",
  clinical: "Health answers",
};

/** What the dialog shows before the schedule has the booking (a review list opened from far away). */
export interface IntakeReviewBookingSummary {
  readonly patientDisplayName: string;
  readonly startAt: string;
}

// ---------------------------------------------------------------------------
// The numbered steps
// ---------------------------------------------------------------------------

/** Done (a check), still to do (its number, in blue), or nothing to do here (a quiet number). */
type StepState = "done" | "todo" | "idle";

interface StepStatus {
  readonly state: StepState;
  /** A short reason shown beside a quiet step ("After step 1", "No changes"). */
  readonly label?: string | undefined;
}

const DONE: StepStatus = { state: "done" };
const TODO: StepStatus = { state: "todo" };
const IDLE: StepStatus = { state: "idle" };

/**
 * The step's state in words: a "Done" chip, a quiet reason, or (to do) words for screen readers
 * only, since the blue number already shows it. Amber stays for real warnings.
 */
function StepWord({ status }: { readonly status: StepStatus }) {
  if (status.label !== undefined) {
    return (
      <span className="intake-review-step-state" data-state={status.state}>
        {status.label}
      </span>
    );
  }
  if (status.state === "done") {
    return (
      <span className="intake-review-step-state" data-state="done">
        Done
      </span>
    );
  }
  return status.state === "todo" ? <span className="sr-only">To do</span> : null;
}

function StepMark({ number, state }: { readonly number: number; readonly state: StepState }) {
  return (
    <span className="intake-review-step-mark" data-state={state} aria-hidden="true">
      {state === "done" ? (
        <Glyph size={13} width={2.6}>
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </Glyph>
      ) : (
        number
      )}
    </span>
  );
}

function Step({
  number,
  title,
  status,
  current,
  children,
}: {
  readonly number: number;
  readonly title: string;
  readonly status: StepStatus;
  /** The first step still to do: what staff do next. */
  readonly current: boolean;
  readonly children: ReactNode;
}) {
  const titleId = useId();
  return (
    <li className="intake-review-step-item">
      <section
        className="intake-review-step"
        data-state={status.state}
        data-current={current ? "" : undefined}
        aria-labelledby={titleId}
      >
        <header className="intake-review-step-head">
          <StepMark number={number} state={status.state} />
          <h3 id={titleId}>{title}</h3>
          <StepWord status={status} />
        </header>
        <div className="intake-review-step-body">{children}</div>
      </section>
    </li>
  );
}

/**
 * A section's step. Once reviewed it is done (unless a new change is chosen); while the chart is
 * still to link, or still loading, it waits; otherwise it is to do only when it holds a choice or
 * a check, and else says so in two words and lets Finish mark it.
 */
function sectionStep(input: {
  readonly review: SchedulingIntakeSectionReview;
  readonly pending: number;
  readonly afterChart: boolean;
  readonly chartLoading: boolean;
  readonly actionable: boolean;
  readonly quietLabel: string;
}): StepStatus {
  const { review } = input;
  if (review.state === "expired") return { state: "idle", label: sectionReviewLabel(review) };
  if (review.state === "reviewed") return input.pending > 0 ? TODO : DONE;
  if (input.pending > 0) return TODO;
  if (input.afterChart) return { state: "idle", label: "After step 1" };
  if (input.chartLoading) return IDLE;
  return input.actionable ? TODO : { state: "idle", label: input.quietLabel };
}

// ---------------------------------------------------------------------------
// History, agreements and messages (collapsed under the steps)
// ---------------------------------------------------------------------------

/**
 * The visit's agreements and messages. consents is what the questionnaire or online booking
 * recorded on this visit; the texts and emails lines are the decision in force now (PA-8, owner
 * decision O1): a staff "Patient agreed" / "Patient said no" or a decision on the chart or another
 * visit, so the review never shows a refusal staff have since replaced.
 */
function AgreementAndMessages({
  review,
  appointmentId,
  timeZone,
}: {
  readonly review: SchedulingIntakeReview;
  readonly appointmentId: string;
  readonly timeZone: string;
}) {
  const at = (instant: string) =>
    formatInClinic(instant, timeZone, { dateStyle: "medium", timeStyle: "short" });
  return (
    <div className="intake-review-more-lists">
      {review.consents.length === 0 ? (
        <p className="intake-review-note">No agreement recorded.</p>
      ) : (
        <ul className="intake-review-lines">
          {review.consents.map((consent) => (
            <li key={consent.kind}>
              <strong>{CONSENT_KIND_LABELS[consent.kind]}</strong>
              <span>
                {consent.decision === "accepted" ? "Agreed" : "Declined"} · version{" "}
                {consent.documentVersion} · {at(consent.recordedAt)} ·{" "}
                {consent.channel === "online_booking"
                  ? "Online booking"
                  : "Pre-visit questionnaire"}
              </span>
            </li>
          ))}
        </ul>
      )}
      <ul className="intake-review-lines" aria-label="Texts and emails agreement">
        {(["sms", "email"] as const).map((channel) => {
          const channelConsent = review.messagingConsent[channel];
          const decision = channelConsent.decision;
          return (
            <li key={channel}>
              <strong>{channel === "sms" ? "Texts" : "Emails"}</strong>
              <span>
                {[
                  MESSAGING_CONSENT_STATE_LABELS[channelConsent.state],
                  ...(decision === null
                    ? []
                    : [
                        `${messagingDecisionPhrase(decision.decision, decision.source).toLowerCase()}${
                          decision.recordedByName === null
                            ? ""
                            : ` (recorded by ${decision.recordedByName})`
                        }`,
                        at(decision.decidedAt),
                        ...(decision.appointmentId === null
                          ? ["on the chart"]
                          : decision.appointmentId !== appointmentId
                            ? ["on another visit"]
                            : []),
                      ]),
                ].join(" · ")}
              </span>
            </li>
          );
        })}
      </ul>
      {review.messages.length === 0 ? (
        <p className="intake-review-note">No messages.</p>
      ) : (
        <ul className="intake-review-lines">
          {review.messages.map((message) => (
            <li key={message.id}>
              <strong>{PATIENT_MESSAGE_KIND_LABELS[message.kind]}</strong>
              <span>
                {PATIENT_MESSAGE_CHANNEL_LABELS[message.channel]} ·{" "}
                {PATIENT_MESSAGE_STATUS_WORDS[message.status]} ·{" "}
                {message.status === "queued"
                  ? `due ${at(message.scheduledFor)}`
                  : at(message.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AppliedHistory({
  submission,
  timeZone,
}: {
  readonly submission: SchedulingIntakeSubmission;
  readonly timeZone: string;
}) {
  return (
    <ul className="intake-review-lines">
      {submission.applications.map((application) => (
        <li key={application.id}>
          <strong>{applicationLabel(application)}</strong>
          <span>
            {application.appliedByName} ·{" "}
            {formatInClinic(application.appliedAt, timeZone, {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Finish: every chosen change, then the reviewed marks, in one click
// ---------------------------------------------------------------------------

interface FinishPlan {
  /** The booking's version when the patient's reason is to become the booking reason. */
  readonly reasonVersion: number | undefined;
  readonly fields: readonly IntakeDemographicField[];
  readonly patientVersion: number | undefined;
  readonly entries: readonly {
    readonly section: IntakeChartEntrySection;
    readonly entries: readonly string[];
  }[];
  readonly marks: readonly SchedulingIntakeSection[];
}

const EMPTY_PLAN: FinishPlan = {
  reasonVersion: undefined,
  fields: [],
  patientVersion: undefined,
  entries: [],
  marks: [],
};

/**
 * "Review intake" (PA-6 / EXECDEV-214): the patient's pre-visit questionnaire beside the chart,
 * with no AI anywhere, as a numbered checklist in one wide Schedule dialog for Schedule, Today and
 * the Lobby. Each step shows a check once done, else its number.
 *
 * 1. Chart: the linked chart on one line, or the one chart path (IntakeChartReconciler) as the
 *    only thing to do before arrival. A walk-in's chart is fixed; other charts its answers match
 *    are listed for information.
 * 2. Details (any scheduling role): only the identity and coverage answers that differ from the
 *    chart or need a check, each with "Use patient's" / "Keep chart's"; one quiet line for every
 *    detail that matches. The reason for visit can become the booking reason before arrival.
 * 3. Health answers (physicians and nurses; the server leaves them out for everyone else):
 *    allergies, medications and history ticked to add word for word as patient-reported entries
 *    marked "Patient intake"; the chart's own editors make corrections. Symptoms and pregnancy
 *    are shown only.
 * 4. Card photo, when the patient sent one from their link (PA-13): its bytes are read only when
 *    it is put on screen.
 * 5. Finish: one button that says exactly what it does ("Apply 2 changes and mark details
 *    reviewed", "Mark health answers reviewed"). It runs the separate applies (each against its
 *    version, with its own retry key), then marks each section chosen, and stops at the first
 *    refusal, shown beside the button. When two sections wait (a physician or nurse before the
 *    desk), both are listed with a tick each, so nobody marks the desk's details unawares.
 *    Marking needs a linked chart before arrival, as check-in does, and waits for the linked
 *    chart to be read, so nothing is marked before its choices were shown.
 *
 * Each step shows a check once done, its number in blue while it needs action (the first of those
 * is outlined as the next one), or a quiet reason ("After step 1", "No changes").
 *
 * Once a section is reviewed nothing is chosen for it when the review opens, so a later reviewer
 * never re-applies what the first one kept. The questionnaire is read (and audited) each time the
 * dialog opens and after each change made here, never on the schedule poll. Times are on the
 * clinic's clock. Once the review closes (the end of the clinic's day the visit ended)
 * everything is read only.
 */
export function IntakeReviewDialog({
  appointmentId,
  client,
  siteId,
  summary,
  onClose,
  onOpenChart,
}: {
  readonly appointmentId: string;
  readonly client: IntakeReviewClient;
  readonly siteId: string;
  readonly summary?: IntakeReviewBookingSummary | undefined;
  readonly onClose: () => void;
  readonly onOpenChart?: ((patientId: string, displayName: string) => void) | undefined;
}) {
  const scheduling = useDemoScheduling();
  const queryClient = useQueryClient();
  const appointment = scheduling.state?.appointments.find(
    (candidate) => candidate.id === appointmentId,
  );
  // A walk-in's own row (PA-5): always on a chart, never a booking to link or edit.
  const walkIn =
    appointment === undefined
      ? scheduling.state?.walkIns?.find((candidate) => candidate.id === appointmentId)
      : undefined;
  const intakeKey = schedulingIntakeKey(siteId, appointmentId);
  const intake = useQuery({
    queryKey: intakeKey,
    queryFn: ({ signal }) => client.getSchedulingIntakeReview(appointmentId, signal),
    ...AUDITED_READ,
  });
  const linkedId = appointment?.patient.patientId ?? walkIn?.patientId;
  const patientId = linkedId !== undefined && CHART_ID.test(linkedId) ? linkedId : null;
  const profileQuery = usePatientProfileQuery(client, siteId, patientId);
  const profile = profileQuery.data;
  const demographics = profile?.demographics;
  const timeZone =
    intake.data?.timeZone ?? scheduling.state?.configuration.timeZone ?? "America/Toronto";
  const at = (instant: string) =>
    formatInClinic(instant, timeZone, { dateStyle: "medium", timeStyle: "short" });
  const [chartNotice, setChartNotice] = useState<string | null>(null);
  const chartNoticeRef = useRef<HTMLParagraphElement>(null);
  const finishNoteRef = useRef<HTMLParagraphElement>(null);
  const finishErrorRef = useRef<HTMLParagraphElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // After a reconcile or a finish the pressed button may be gone: focus moves to what happened,
  // once it is on screen (a new object each time, so the same target can be focused again).
  const [focusTarget, setFocusTarget] = useState<{
    readonly target: "chart" | "finish" | "finish-error" | "close";
  } | null>(null);
  useEffect(() => {
    if (focusTarget === null) return;
    const refs = {
      chart: chartNoticeRef,
      finish: finishNoteRef,
      "finish-error": finishErrorRef,
      close: closeRef,
    } as const;
    refs[focusTarget.target].current?.focus();
  }, [focusTarget]);
  const dirtyEditors = useRef(new Set<string>());
  // Escape first backs out of a question the chart reconciler asks in place.
  const reconcilerEscape = useRef<(() => boolean) | null>(null);
  const onDirtyChange = useCallback((key: string, dirty: boolean) => {
    if (dirty) dirtyEditors.current.add(key);
    else dirtyEditors.current.delete(key);
  }, []);
  // Unsaved chart edits are asked about in the dialog's footer, focused on "Keep editing".
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const close = () => {
    if (dirtyEditors.current.size > 0) {
      setConfirmingDiscard(true);
      return;
    }
    onClose();
  };
  const keepEditing = () => {
    setConfirmingDiscard(false);
    setFocusTarget({ target: "close" });
  };

  /** After a change made here: the review, the chart and the schedule's pills catch up. */
  const refresh = async (options: { readonly chart?: string | null } = {}) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: intakeKey }),
      queryClient.invalidateQueries({ queryKey: ["scheduling-intake-reviews", siteId] }),
      options.chart ? invalidatePatientQueries(queryClient, siteId, options.chart) : undefined,
      scheduling.refresh().catch(() => undefined),
    ]);
  };

  const reconciled = async (
    result: ReconcileSchedulingPatientResponse,
    request: ReconcileSchedulingPatientRequest,
  ) => {
    setChartNotice(
      result.outcome === "created"
        ? `Chart created (MRN ${result.mrn}) and linked.`
        : result.outcome === "linked"
          ? request.action === "link" && request.correctBookingDateOfBirth === true
            ? "Chart linked. The booking now has the chart's date of birth."
            : "Chart linked."
          : "Ruled out. No chart created.",
    );
    await Promise.all([
      refresh({ chart: result.outcome === "mismatch" ? null : result.patientId }),
      queryClient.invalidateQueries({
        queryKey: schedulingPatientMatchesKey(siteId, appointmentId),
      }),
    ]);
    // The button that was pressed is gone: land on what happened.
    setFocusTarget({ target: "chart" });
  };

  const chartName =
    demographics === undefined
      ? undefined
      : [demographics.legalFirstName, demographics.legalLastName].join(" ");
  // A walk-in's row carries no patient details (PA-5): name it from its chart.
  const name =
    appointment?.patient.displayName ??
    summary?.patientDisplayName ??
    (walkIn === undefined ? "This booking" : (chartName ?? "This walk-in"));
  const startAt = appointment?.startAt ?? walkIn?.arrivedAt ?? summary?.startAt;
  const description = [
    name,
    appointment ? `DOB ${appointment.patient.dateOfBirth}` : undefined,
    startAt === undefined
      ? undefined
      : `${walkIn === undefined ? "visit" : "walked in"} ${at(startAt)}`,
  ]
    .filter((part) => part !== undefined)
    .join(" · ");

  const review = intake.data;
  const submission = review?.submission ?? null;
  const administrative = submission?.administrative;
  const clinical = submission?.clinical;
  const open = submission?.open ?? false;
  const beforeArrival =
    appointment !== undefined && ["booked", "confirmed"].includes(appointment.status);
  // Before arrival a booking without a chart can still get one, and needs it: check-in requires
  // it, and no health answer can be added without it. A visit cancelled or marked no-show without
  // a chart can never take one, so its sections can be marked as they are.
  const chartStillNeeded = beforeArrival && appointment?.patient.patientId === undefined;
  const chartLinked = patientId !== null || appointment?.patient.patientId !== undefined;
  const canLink = !chartLinked && open && beforeArrival;

  // Step 4's photo list: read once the questionnaire is on screen, as its panel used to be.
  const { listAppointmentHealthCardPhotos, openHealthCardPhoto } = client;
  const photoRoutes = useMemo(
    () =>
      listAppointmentHealthCardPhotos !== undefined && openHealthCardPhoto !== undefined
        ? { listAppointmentHealthCardPhotos, openHealthCardPhoto }
        : undefined,
    [listAppointmentHealthCardPhotos, openHealthCardPhoto],
  );
  const photos = useIntakeHealthCardPhotos(appointmentId, siteId, photoRoutes, submission !== null);

  // ---- What the steps offer, and what is chosen ------------------------------------------------
  const followUps = review?.healthCardFollowUps;
  const details = useMemo(
    () =>
      administrative === undefined
        ? undefined
        : intakeDetailReview(administrative.answers, demographics, followUps ?? []),
    [administrative, demographics, followUps],
  );
  const proposals = useMemo(
    () =>
      clinical === undefined
        ? undefined
        : {
            allergies: intakeEntryProposals("allergies", clinical.answers, profile?.allergies),
            medications: intakeEntryProposals(
              "medications",
              clinical.answers,
              profile?.medications,
            ),
            pmh: intakeEntryProposals("pmh", clinical.answers, profile?.pmh),
          },
    [clinical, profile],
  );

  // A newly read chart, or a section newly reviewed, starts its choice again. Once a section is
  // reviewed nothing starts chosen: the reviewer already decided what to keep.
  const detailBasis = `${demographics?.version ?? "-"}|${administrative?.review.state ?? "-"}`;
  const [detailPicks, setDetailPicks] = useState<{
    readonly basis: string;
    readonly picked: ReadonlySet<IntakeDemographicField>;
  } | null>(null);
  const detailDefaults = useMemo(
    () =>
      new Set(
        details === undefined || administrative?.review.state === "reviewed"
          ? []
          : details.attention
              .filter(isDetailChoice)
              .filter((line) => line.defaultApply)
              .map((line) => line.field),
      ),
    [details, administrative?.review.state],
  );
  const pickedDetails =
    detailPicks !== null && detailPicks.basis === detailBasis ? detailPicks.picked : detailDefaults;
  const pickDetail = (field: IntakeDemographicField, use: boolean) => {
    const next = new Set(pickedDetails);
    if (use) next.add(field);
    else next.delete(field);
    setDetailPicks({ basis: detailBasis, picked: next });
  };

  const entryBasis = `${patientId ?? "-"}|${clinical?.review.state ?? "-"}`;
  const [entryPicks, setEntryPicks] = useState<{
    readonly basis: string;
    readonly picked: Partial<Record<IntakeChartEntrySection, ReadonlySet<string>>>;
  } | null>(null);
  const pickedEntries = (section: IntakeChartEntrySection): ReadonlySet<string> => {
    const explicit =
      entryPicks !== null && entryPicks.basis === entryBasis
        ? entryPicks.picked[section]
        : undefined;
    if (explicit !== undefined) return explicit;
    if (proposals === undefined || clinical?.review.state === "reviewed") return NO_PICKS;
    return new Set(
      proposals[section].entries.filter((entry) => !entry.onChart).map((entry) => entry.display),
    );
  };
  const pickEntry = (section: IntakeChartEntrySection, display: string, add: boolean) => {
    const next = new Set(pickedEntries(section));
    if (add) next.add(display);
    else next.delete(display);
    const kept = entryPicks !== null && entryPicks.basis === entryBasis ? entryPicks.picked : {};
    setEntryPicks({ basis: entryBasis, picked: { ...kept, [section]: next } });
  };
  const entriesApplicable = (section: IntakeChartEntrySection) =>
    open && patientId !== null && profile?.[section] !== undefined;

  const reasonDecision =
    submission === null
      ? ({ kind: "hidden" } as const)
      : intakeReasonDecision({
          reason: submission.visit.reason,
          booking: appointment,
          walkIn: walkIn !== undefined,
          canApply: open && appointment?.version !== undefined,
        });
  const [reasonPick, setReasonPick] = useState<{
    readonly basis: number | undefined;
    readonly use: boolean;
  } | null>(null);
  const reasonUse =
    reasonPick !== null && reasonPick.basis === appointment?.version ? reasonPick.use : false;

  const canChooseDetails = open && patientId !== null && demographics !== undefined;
  // The linked chart is still being read, or the read failed: nothing is applied or marked until
  // it is on screen, so no default choice is ever marked away unseen (and the button's words
  // never change under the pointer as the chart arrives).
  const chartUnread = patientId !== null && profile === undefined;
  const chartLoading = chartUnread && !profileQuery.isError;
  const awaiting = (["administrative", "clinical"] as const).filter(
    (section) => submission?.[section]?.review.state === "awaiting_review",
  );
  const markable = open && !chartStillNeeded && !chartUnread ? awaiting : [];
  // When two sections wait, each has its own tick (both start ticked): unticked stays to review.
  // The ticks show only then; once one section is left, the button names it.
  const [unmarked, setUnmarked] = useState<ReadonlySet<SchedulingIntakeSection>>(new Set());
  const marking =
    markable.length > 1 ? markable.filter((section) => !unmarked.has(section)) : markable;
  const plan: FinishPlan = chartUnread
    ? EMPTY_PLAN
    : {
        reasonVersion:
          reasonUse && reasonDecision.kind === "choice" ? appointment?.version : undefined,
        fields:
          canChooseDetails && details !== undefined
            ? details.attention
                .filter(isDetailChoice)
                .filter((line) => pickedDetails.has(line.field))
                .map((line) => line.field)
            : [],
        patientVersion: demographics?.version,
        entries:
          proposals === undefined
            ? []
            : ENTRY_SECTIONS.flatMap((section) => {
                if (!entriesApplicable(section)) return [];
                const picked = pickedEntries(section);
                const entries = proposals[section].entries
                  .filter((entry) => !entry.onChart && picked.has(entry.display))
                  .map((entry) => entry.display);
                return entries.length === 0 ? [] : [{ section, entries }];
              }),
        marks: marking,
      };
  const detailChanges = plan.fields.length + (plan.reasonVersion === undefined ? 0 : 1);
  const entryChanges = plan.entries.reduce((count, part) => count + part.entries.length, 0);
  const changes = detailChanges + entryChanges;
  const finishLabel = open ? intakeFinishLabel(changes, plan.marks) : null;
  const markBlocked = open && chartStillNeeded && awaiting.length > 0;

  // ---- Finish -----------------------------------------------------------------------------------
  const [finishing, setFinishing] = useState(false);
  const [finishNotes, setFinishNotes] = useState<readonly string[]>([]);
  const [finishError, setFinishError] = useState<string | null>(null);
  const finishHintId = useId();
  // One retry key per apply: sent again only for a retry after a lost request, replaced once the
  // server answers (so a changed choice is a new apply).
  const applyKeys = useRef(new Map<string, string>());
  const keyFor = (kind: string) => {
    const existing = applyKeys.current.get(kind);
    if (existing !== undefined) return existing;
    const key = newIntakeIdempotencyKey();
    applyKeys.current.set(kind, key);
    return key;
  };
  const settleKey = (kind: string, caught?: unknown) => {
    if (caught === undefined || caught instanceof SchedulingRequestError) {
      applyKeys.current.delete(kind);
    }
  };

  const finish = async (run: FinishPlan) => {
    if (finishing) return;
    setFinishing(true);
    setFinishError(null);
    setFinishNotes([]);
    const notes: string[] = [];
    const added: { section: IntakeChartEntrySection; added: number }[] = [];
    let changed = false;
    let reread = false;
    let completed = false;
    const refused = (title: string, caught: unknown, fallback: string) => {
      setFinishError(`${title}: ${intakeErrorMessage(caught, fallback)}`);
      if (isIntakeTargetChanged(caught) || isIntakeReviewClosed(caught)) reread = true;
    };
    try {
      if (run.reasonVersion !== undefined) {
        try {
          await client.applySchedulingIntakeReason(appointmentId, {
            expectedVersion: run.reasonVersion,
          });
          changed = true;
          notes.push("Booking reason updated.");
        } catch (caught) {
          refused("Reason for visit", caught, "The booking reason could not be updated.");
          return;
        }
      }
      if (run.fields.length > 0 && run.patientVersion !== undefined) {
        try {
          const result = await client.applySchedulingIntakeDemographics(appointmentId, {
            fields: [...run.fields],
            expectedPatientVersion: run.patientVersion,
            idempotencyKey: keyFor("demographics"),
          });
          settleKey("demographics");
          changed = true;
          notes.push(
            result.changedFields.length === 0
              ? "Details: nothing changed, the chart already had them."
              : `Updated on the chart: ${result.changedFields
                  .map((field) =>
                    field in INTAKE_FIELD_LABELS
                      ? INTAKE_FIELD_LABELS[field as IntakeDemographicField]
                      : field,
                  )
                  .join(", ")}.`,
          );
        } catch (caught) {
          settleKey("demographics", caught);
          refused("Details", caught, "The patient's details could not be applied.");
          return;
        }
      }
      for (const part of run.entries) {
        try {
          const result = await client.applySchedulingIntakeChartEntries(appointmentId, {
            section: part.section,
            entries: part.entries.map((display) => ({ display })),
            idempotencyKey: keyFor(part.section),
          });
          settleKey(part.section);
          changed = true;
          added.push({ section: part.section, added: result.added });
        } catch (caught) {
          settleKey(part.section, caught);
          refused(
            CHART_ENTRY_SECTION_LABELS[part.section],
            caught,
            "The entries could not be added to the chart.",
          );
          return;
        }
      }
      for (const section of run.marks) {
        try {
          await client.markSchedulingIntakeSectionReviewed(appointmentId, { section });
          changed = true;
        } catch (caught) {
          refused(SECTION_TITLES[section], caught, "The section could not be marked reviewed.");
          return;
        }
      }
      completed = true;
    } finally {
      // What the entries did is said once, after the details; the mark comes last.
      const entriesNote = intakeEntriesNote(added);
      if (entriesNote !== undefined) notes.push(entriesNote);
      if (completed && run.marks.length > 0) notes.push("Marked reviewed.");
      setFinishNotes(notes);
      if (changed || reread) await refresh({ chart: patientId });
      setFinishing(false);
      // The button may be gone (nothing left, or the review closed): land on what happened.
      setFocusTarget({ target: completed ? "finish" : "finish-error" });
    }
  };

  // ---- Body -------------------------------------------------------------------------------------
  let body: ReactNode;
  let finishStep: ReactNode = null;
  let primary: ReactNode = null;
  if (intake.isPending) {
    body = (
      <p className="intake-review-note" role="status">
        Loading the questionnaire…
      </p>
    );
  } else if (intake.isError || review === undefined) {
    body = (
      <div className="schedule-modal-error" role="alert">
        <span>{intakeErrorMessage(intake.error, "The questionnaire could not be loaded.")}</span>
        <button
          type="button"
          className="schedule-btn ghost sm"
          onClick={() => void intake.refetch()}
        >
          Try again
        </button>
      </div>
    );
  } else if (submission === null || administrative === undefined || details === undefined) {
    body = (
      <>
        <p className="intake-review-note">No questionnaire has been submitted for this booking.</p>
        <section className="intake-review-more-open" aria-label="Texts, emails and agreements">
          <AgreementAndMessages review={review} appointmentId={appointmentId} timeZone={timeZone} />
        </section>
      </>
    );
  } else {
    const reviewedParts = (["administrative", "clinical"] as const).flatMap((section) => {
      const sectionReview = submission[section]?.review;
      if (sectionReview?.state !== "reviewed") return [];
      return [
        `${SECTION_TITLES[section]} reviewed by ${sectionReview.reviewedByName ?? "a staff member"}${
          sectionReview.reviewedAt === undefined ? "" : `, ${at(sectionReview.reviewedAt)}`
        }`,
      ];
    });
    const photoShown = photoRoutes !== undefined && intakeHealthCardPhotoShown(photos);
    let number = 0;
    const chartStatus: StepStatus = chartLinked ? DONE : canLink ? TODO : IDLE;
    // Details hold something to do when a detail can be chosen, the reason can become the
    // booking's, or the health card needs a check at the desk.
    const detailsActionable =
      open &&
      ((canChooseDetails && details.attention.some(isDetailChoice)) ||
        reasonDecision.kind === "choice" ||
        (followUps ?? []).length > 0);
    const detailsStatus = sectionStep({
      review: administrative.review,
      pending: detailChanges,
      afterChart: canLink,
      chartLoading: chartUnread,
      actionable: detailsActionable,
      quietLabel: "No changes",
    });
    const healthStatus: StepStatus =
      clinical === undefined || proposals === undefined
        ? IDLE
        : sectionStep({
            review: clinical.review,
            pending: entryChanges,
            afterChart: canLink,
            chartLoading: chartUnread,
            actionable:
              open &&
              ENTRY_SECTIONS.some(
                (section) =>
                  entriesApplicable(section) &&
                  proposals[section].entries.some((entry) => !entry.onChart),
              ),
            quietLabel: "Nothing to add",
          });
    const everyReviewed = awaiting.length === 0 && administrative.review.state === "reviewed";
    // Finish is to do while its button has something to send, done once nothing is left, and
    // waits otherwise (for step 1, or for the chart to load).
    const finishStatus: StepStatus = open
      ? finishLabel !== null
        ? TODO
        : awaiting.length === 0 && changes === 0
          ? DONE
          : IDLE
      : everyReviewed
        ? DONE
        : IDLE;
    const statuses = [chartStatus, detailsStatus, healthStatus, finishStatus];
    const current = statuses.findIndex((status) => status.state === "todo");

    body = (
      <>
        <div className="intake-review-lede">
          <p className="intake-review-lede-main">
            {open
              ? "Nothing changes on the chart until you apply it."
              : "This review has closed: read only."}
          </p>
          <p className="intake-review-meta">
            {[`Submitted ${at(submission.submittedAt)}`, ...reviewedParts].join(" · ")}
          </p>
        </div>
        <ol className="intake-review-steps">
          <Step number={++number} title="Chart" status={chartStatus} current={current === 0}>
            {patientId !== null ? (
              <>
                <div className="intake-review-linked">
                  <p className="intake-review-chart-line">
                    <strong>{chartName ?? name}</strong>
                    {demographics ? <span>{` · MRN ${demographics.mrn}`}</span> : null}
                  </p>
                  {onOpenChart ? (
                    <button
                      type="button"
                      className="schedule-btn ghost sm"
                      onClick={() => onOpenChart(patientId, chartName ?? name)}
                    >
                      Open chart
                    </button>
                  ) : null}
                </div>
                {walkIn !== undefined ? (
                  <WalkInChartDuplicates
                    appointmentId={appointmentId}
                    client={client}
                    siteId={siteId}
                    timeZone={timeZone}
                    onOpenChart={onOpenChart}
                  />
                ) : null}
              </>
            ) : chartLinked ? (
              <p className="intake-review-note">This booking is linked to a chart.</p>
            ) : canLink ? (
              <IntakeChartReconciler
                appointmentId={appointmentId}
                expectedVersion={appointment?.version}
                identityAvailable={administrative.answers.identity !== null}
                bookingDateOfBirth={appointment?.patient.dateOfBirth}
                timeZone={timeZone}
                client={client}
                siteId={siteId}
                escapeHandler={reconcilerEscape}
                onReconciled={reconciled}
              />
            ) : (
              <p className="intake-review-note">
                No chart linked. Charts are linked before arrival.
              </p>
            )}
            {chartNotice ? (
              <p className="intake-review-notice" role="status" tabIndex={-1} ref={chartNoticeRef}>
                {chartNotice}
              </p>
            ) : null}
          </Step>
          <Step
            number={++number}
            title={SECTION_TITLES.administrative}
            status={detailsStatus}
            current={current === 1}
          >
            <IntakeDetailsStep
              details={details}
              identityAsked={administrative.answers.identity !== null}
              linked={patientId !== null}
              chartState={
                demographics !== undefined ? "ready" : profileQuery.isError ? "error" : "loading"
              }
              unlinkedNote={
                chartLinked
                  ? "This chart can't be compared here."
                  : canLink
                    ? "Link the chart first (step 1) to compare."
                    : "No chart to compare with."
              }
              canChoose={canChooseDetails}
              picked={pickedDetails}
              busy={finishing}
              onPick={pickDetail}
              reason={{
                text: submission.visit.reason,
                decision: reasonDecision,
                use: reasonUse,
                onPick: (use) => setReasonPick({ basis: appointment?.version, use }),
              }}
              editor={
                patientId !== null && open ? (
                  <ChartSectionPanel
                    sectionKey="demographics"
                    profile={profile}
                    loadError={profileQuery.isError}
                    client={client}
                    siteId={siteId}
                    patientId={patientId}
                    readBody="hidden"
                    editLabel="Edit details on the chart"
                    onDirtyChange={onDirtyChange}
                  />
                ) : undefined
              }
              onRetryChart={() => void profileQuery.refetch()}
            />
          </Step>
          <Step
            number={++number}
            title={SECTION_TITLES.clinical}
            status={healthStatus}
            current={current === 2}
          >
            {clinical === undefined || proposals === undefined ? (
              <p className="intake-review-note">A physician or nurse reviews these.</p>
            ) : (
              <div className="intake-review-health">
                {patientId !== null ? null : (
                  <p className="intake-review-note">
                    {chartLinked
                      ? "This chart can't be read here: nothing can be added."
                      : canLink
                        ? "Link the chart first (step 1) to add these."
                        : "No chart linked: nothing can be added."}
                  </p>
                )}
                {ENTRY_SECTIONS.map((section) => (
                  <IntakeEntriesPanel
                    key={section}
                    section={section}
                    proposals={proposals[section]}
                    picked={pickedEntries(section)}
                    canApply={entriesApplicable(section)}
                    busy={finishing}
                    profile={profile}
                    profileLoadError={profileQuery.isError}
                    patientId={patientId}
                    siteId={siteId}
                    client={client}
                    onPick={(display, add) => pickEntry(section, display, add)}
                    onDirtyChange={onDirtyChange}
                  />
                ))}
                <IntakeVisitContextPanel answers={clinical.answers} />
              </div>
            )}
          </Step>
          {photoShown && photoRoutes !== undefined ? (
            <Step number={++number} title="Card photo" status={IDLE} current={false}>
              <IntakeHealthCardPhoto photos={photos} timeZone={timeZone} client={photoRoutes} />
            </Step>
          ) : null}
        </ol>
        <div className="intake-review-more">
          {submission.applications.length > 0 ? (
            <IntakeDisclosure
              label={`Applied so far (${submission.applications.length})`}
              regionLabel="Applied so far"
            >
              <AppliedHistory submission={submission} timeZone={timeZone} />
            </IntakeDisclosure>
          ) : null}
          <IntakeDisclosure label="Texts, emails and agreements">
            <AgreementAndMessages
              review={review}
              appointmentId={appointmentId}
              timeZone={timeZone}
            />
          </IntakeDisclosure>
        </div>
      </>
    );

    const hint = !open
      ? undefined
      : chartLoading
        ? "Loading the chart…"
        : chartUnread
          ? "The chart couldn't be loaded: try again in step 2."
          : markBlocked
            ? changes > 0
              ? "Mark reviewed once the chart is linked (step 1)."
              : "Next: link or create the chart (step 1)."
            : undefined;
    // A refusal (or what was done before it) stays in view even when it closed the review.
    if (open || finishError !== null || finishNotes.length > 0) {
      finishStep = (
        <FinishStep
          number={number + 1}
          status={finishStatus}
          current={current === 3}
          hint={hint}
          hintId={finishHintId}
          marks={
            markable.length > 1
              ? {
                  sections: markable,
                  unmarked,
                  disabled: finishing,
                  onChange: (section, mark) => {
                    const next = new Set(unmarked);
                    if (mark) next.delete(section);
                    else next.add(section);
                    setUnmarked(next);
                  },
                }
              : undefined
          }
          notes={finishNotes}
          error={finishError}
          noteRef={finishNoteRef}
          errorRef={finishErrorRef}
        />
      );
    }
    // One primary action, and only when it can run: never a disabled "Nothing chosen".
    primary =
      finishLabel !== null ? (
        <button
          type="button"
          className="schedule-btn primary"
          disabled={finishing}
          aria-describedby={hint === undefined ? undefined : finishHintId}
          onClick={() => void finish(plan)}
        >
          {finishing ? "Saving…" : finishLabel}
        </button>
      ) : null;
  }

  return (
    <ScheduleModal
      size="wide"
      title="Review intake"
      description={description}
      onEscape={() => {
        if (confirmingDiscard) {
          keepEditing();
          return true;
        }
        return reconcilerEscape.current?.() === true;
      }}
      onClose={close}
    >
      <div className="intake-review">{body}</div>
      <div className="schedule-modal-actions intake-review-finish">
        {confirmingDiscard ? (
          <DiscardQuestion onKeep={keepEditing} onDiscard={onClose} />
        ) : (
          <>
            {finishStep}
            <div className="intake-review-finish-actions">
              <button ref={closeRef} type="button" className="schedule-btn ghost" onClick={close}>
                Close
              </button>
              {primary}
            </div>
          </>
        )}
      </div>
    </ScheduleModal>
  );
}

/** The marks one finish sends when two sections wait: each ticked, so each is a choice. */
interface FinishMarks {
  readonly sections: readonly SchedulingIntakeSection[];
  readonly unmarked: ReadonlySet<SchedulingIntakeSection>;
  readonly disabled: boolean;
  readonly onChange: (section: SchedulingIntakeSection, mark: boolean) => void;
}

/** The last step: what happened (a live line), why it waits, or the refusal that stopped it. */
function FinishStep({
  number,
  status,
  current,
  hint,
  hintId,
  marks,
  notes,
  error,
  noteRef,
  errorRef,
}: {
  readonly number: number;
  readonly status: StepStatus;
  readonly current: boolean;
  readonly hint: string | undefined;
  readonly hintId: string;
  readonly marks: FinishMarks | undefined;
  readonly notes: readonly string[];
  readonly error: string | null;
  readonly noteRef: RefObject<HTMLParagraphElement | null>;
  readonly errorRef: RefObject<HTMLParagraphElement | null>;
}) {
  const titleId = useId();
  return (
    <section
      className="intake-review-finish-step"
      data-state={status.state}
      data-current={current ? "" : undefined}
      aria-labelledby={titleId}
    >
      <header className="intake-review-step-head">
        <StepMark number={number} state={status.state} />
        <h3 id={titleId}>Finish</h3>
        <StepWord status={status} />
      </header>
      <div className="intake-review-finish-text">
        {marks === undefined ? null : (
          <fieldset className="intake-review-marks" disabled={marks.disabled}>
            <legend>Mark reviewed</legend>
            {marks.sections.map((section) => (
              <label key={section} className="schedule-modal-check">
                <input
                  type="checkbox"
                  checked={!marks.unmarked.has(section)}
                  onChange={(event) => marks.onChange(section, event.target.checked)}
                />
                <span>{SECTION_TITLES[section]}</span>
              </label>
            ))}
          </fieldset>
        )}
        {hint === undefined ? null : (
          <p className="intake-review-note" id={hintId}>
            {hint}
          </p>
        )}
        <p className="intake-review-notice" role="status" tabIndex={-1} ref={noteRef}>
          {notes.join(" ")}
        </p>
        {error === null ? null : (
          <p className="schedule-modal-error" role="alert" tabIndex={-1} ref={errorRef}>
            {error}
          </p>
        )}
      </div>
    </section>
  );
}

/** Closing with unsaved chart edits: asked in place, focused on the safe choice. */
function DiscardQuestion({
  onKeep,
  onDiscard,
}: {
  readonly onKeep: () => void;
  readonly onDiscard: () => void;
}) {
  const keepRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    keepRef.current?.focus();
  }, []);
  return (
    <fieldset className="intake-review-discard">
      <legend className="intake-review-discard-title">Discard your unsaved chart edits?</legend>
      <div className="intake-review-finish-actions">
        <button ref={keepRef} type="button" className="schedule-btn ghost" onClick={onKeep}>
          Keep editing
        </button>
        <button type="button" className="schedule-btn primary" onClick={onDiscard}>
          Discard and close
        </button>
      </div>
    </fieldset>
  );
}
